import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowRight } from "react-icons/fa6";
import {
  HiOutlineViewBoards,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineLockClosed,
  HiOutlinePlus,
  HiOutlineUserGroup,
} from "react-icons/hi";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import {
  useGetTodoBoards,
  useCreateTodoBoard,
  useRenameTodoBoard,
  useDeleteTodoBoard,
} from "../../../../hooks/todos";
import {
  TodoBoardSummary,
  MAX_TODO_BOARD_NAME_LENGTH,
  todoBoardLimit,
} from "../../../../types/TodoTypes";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { TextInput } from "flowbite-react";
import { FieldLabel } from "../../../atoms/FieldLabel";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import NoTranslate from "../../../atoms/NoTranslate";
import ConfirmModal from "../../../public/ConfirmModal";
import CollapsibleHowItWorks from "../../../common/CollapsibleHowItWorks";
import TodoBoard from "./TodoBoard";
import BoardAccessModal from "./BoardAccessModal";
import BoardAccessBadge from "./BoardAccessBadge";
import TodosGettingStarted from "./TodosGettingStarted";
import { TODOS_BASE_PATH, boardPath } from "./todoUrl";

const TodoBoards: React.FC = () => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const entityId = user.entity_identification;
  const navigate = useNavigate();

  const { boardId: openBoardId, shortId: openShortId } = useParams();

  const hasAccess = permissions.hasFeatureAccess("TODOS_MANAGEMENT");
  const canOpenBoard = permissions.can(Cap.TASKS_BOARD_TASKS, Level.VIEW);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState<TodoBoardSummary | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [toDelete, setToDelete] = useState<TodoBoardSummary | null>(null);
  const [sharingBoardId, setSharingBoardId] = useState<string | null>(null);

  const { data: boards = [], isLoading } = useGetTodoBoards(
    customer_identification,
    entityId
  );
  const createBoard = useCreateTodoBoard(customer_identification, entityId);
  const renameBoard = useRenameTodoBoard(customer_identification, entityId);
  const deleteBoard = useDeleteTodoBoard(customer_identification, entityId);

  useEffect(() => {
    if (!openBoardId || !hasAccess || canOpenBoard) return;
    toast.error(t("todos.boardLocked") as string);
    navigate(TODOS_BASE_PATH, { replace: true });
  }, [openBoardId, hasAccess, canOpenBoard, navigate, t]);

  if (!hasAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-lg border border-gray-200 p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-amber-50 rounded-full flex items-center justify-center">
            <HiOutlineLockClosed className="w-6 h-6 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t("todos.lock.title")}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("todos.lock.description")}
          </p>
        </div>
      </div>
    );
  }

  if (openBoardId && canOpenBoard) {
    return (
      <TodoBoard
        boardId={openBoardId}
        shortId={openShortId}
        onBack={() => navigate(TODOS_BASE_PATH)}
      />
    );
  }

  const handleCreate = () => {
    if (!permissions.can(Cap.TASKS_BOARDS, Level.WRITE)) return;
    const name = newName.trim();
    if (!name) return;
    createBoard.mutate(
      { name },
      {
        onSuccess: () => {
          setShowCreate(false);
          setNewName("");
        },
        onError: (err) => {
          toast.error(
            (err?.response?.data?.message || t("todos.saveError")) as string
          );
        },
      }
    );
  };

  const handleRename = () => {
    if (!permissions.can(Cap.TASKS_BOARDS, Level.WRITE)) return;
    if (!renaming) return;
    const name = renameValue.trim();
    if (!name) return;
    renameBoard.mutate(
      { boardId: renaming.board_id, name },
      {
        onSuccess: () => setRenaming(null),
        onError: (err) => {
          toast.error(
            (err?.response?.data?.message || t("todos.saveError")) as string
          );
        },
      }
    );
  };

  const handleDelete = () => {
    if (!permissions.can(Cap.TASKS_BOARDS, Level.DELETE)) return;
    if (!toDelete) return;
    deleteBoard.mutate(toDelete.board_id, {
      onSettled: () => setToDelete(null),
    });
  };

  const boardLimit = todoBoardLimit(permissions.getPlanTier());
  const atBoardCap = boards.length >= boardLimit;

  const openPlans = () => window.dispatchEvent(new Event("openPlansModal"));

  return (
    <div className="container mx-auto py-6 px-3">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("todos.pageTitle")}
          </h1>
          {!isLoading && boards.length > 0 && (
            <p className="mt-0.5 text-sm text-gray-500">
              {t("todos.boardCount", { count: boards.length })}
            </p>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-400 py-16">
          {t("todos.loading")}
        </div>
      ) : boards.length === 0 ? (
        <>
          <TodosGettingStarted />
          {permissions.can(Cap.TASKS_BOARDS, Level.WRITE) && (
            <div className="flex justify-center mt-6">
              <CustomButton
                variant="primary"
                icon="plus"
                title={t("todos.createBoard") as string}
                onClick={() => setShowCreate(true)}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <CollapsibleHowItWorks
            collapsible
            title={t("todos.howItWorksToggle")}
            icon={<HiOutlineViewBoards className="text-blue-600" size={18} />}
            className="mb-4"
          >
            <TodosGettingStarted />
          </CollapsibleHowItWorks>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {boards.map((b) => {
              const columnsWithTasks = b.columns.filter(
                (c) => (b.column_counts?.[c.id] ?? 0) > 0
              );
              return (
                <div
                  key={b.board_id}
                  className="group flex flex-col bg-white border border-gray-200 rounded-lg p-4 transition-colors hover:border-gray-300"
                >
                  <button
                    type="button"
                    onClick={() =>
                      canOpenBoard && navigate(boardPath(b.board_id))
                    }
                    disabled={!canOpenBoard}
                    aria-disabled={!canOpenBoard}
                    title={
                      canOpenBoard
                        ? undefined
                        : (t("todos.boardLocked") as string)
                    }
                    className={`w-full text-left ${
                      canOpenBoard ? "" : "cursor-not-allowed"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gray-50 text-gray-600 flex items-center justify-center shrink-0">
                        <HiOutlineViewBoards size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-gray-900 transition-colors group-hover:text-blue-700">
                          <span className="truncate">
                            <NoTranslate>{b.name}</NoTranslate>
                          </span>
                          {!canOpenBoard && (
                            <HiOutlineLockClosed
                              size={13}
                              className="text-gray-400 shrink-0"
                              title={t("todos.boardLocked") as string}
                            />
                          )}
                          <FaArrowRight className="w-2.5 h-2.5 shrink-0 text-gray-300 transition-colors group-hover:text-blue-700" />
                        </h3>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                          <span>
                            {t("todos.taskCount", { count: b.task_count })}
                          </span>
                          <BoardAccessBadge access={b.access} />
                        </p>
                      </div>
                    </div>

                    {columnsWithTasks.length > 0 && (
                      <div className="mt-3 grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                        {columnsWithTasks.map((c) => (
                          <React.Fragment key={c.id}>
                            <span className="truncate text-gray-500">
                              <NoTranslate>{c.label}</NoTranslate>
                            </span>
                            <span className="tabular-nums text-gray-700">
                              <NoTranslate>
                                {b.column_counts?.[c.id] ?? 0}
                              </NoTranslate>
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </button>

                  {/* Actions toujours visibles, jamais revelees au survol.
                      Une fonction qu'on ne voit pas n'existe pas, et surtout le
                      survol n'existe pas au doigt : une tablette en paysage
                      depasse le seuil md et rendait ces trois actions
                      inatteignables. Elles restent discretes par la couleur,
                      pas par la disparition. */}
                  <div className="flex justify-end gap-1 mt-auto pt-3">
                    {permissions.can(Cap.TASKS_BOARDS, Level.WRITE) && (
                      <CustomButton
                        type="button"
                        variant="none"
                        size="icon"
                        onClick={() => {
                          setRenaming(b);
                          setRenameValue(b.name);
                        }}
                        className="rounded-md text-gray-500 enabled:hover:bg-blue-50 enabled:hover:text-blue-600"
                        icon={<HiOutlinePencil size={18} />}
                        aria-label={t("todos.renameBoard") as string}
                      />
                    )}
                    {permissions.can(Cap.TASKS_BOARDS, Level.DELETE) && (
                      <CustomButton
                        type="button"
                        variant="none"
                        size="icon"
                        onClick={() => setSharingBoardId(b.board_id)}
                        className="rounded-md text-gray-500 enabled:hover:bg-blue-50 enabled:hover:text-blue-600"
                        icon={<HiOutlineUserGroup size={18} />}
                        aria-label={t("todos.access.manage") as string}
                        tooltip={t("todos.access.manage") as string}
                      />
                    )}
                    {permissions.can(Cap.TASKS_BOARDS, Level.DELETE) && (
                      <CustomButton
                        type="button"
                        variant="none"
                        size="icon"
                        onClick={() => setToDelete(b)}
                        className="rounded-md text-gray-500 enabled:hover:bg-red-50 enabled:hover:text-red-600"
                        icon={<HiOutlineTrash size={18} />}
                        aria-label={t("todos.deleteBoard") as string}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            {atBoardCap ? (
              <button
                type="button"
                onClick={openPlans}
                className="min-h-[120px] flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 text-gray-400 transition-colors hover:border-amber-300 hover:bg-amber-50/40"
              >
                <UnlockBadge onClick={openPlans} />
                <span className="text-xs text-gray-400 text-center px-3">
                  {t("todos.boardLimitReached", { limit: boardLimit })}
                </span>
              </button>
            ) : (
              permissions.can(Cap.TASKS_BOARDS, Level.WRITE) && (
                <button
                  type="button"
                  onClick={() => setShowCreate(true)}
                  className="min-h-[120px] flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700"
                >
                  <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center">
                    <HiOutlinePlus size={18} />
                  </div>
                  <span className="text-sm font-medium">
                    {t("todos.createBoard")}
                  </span>
                </button>
              )
            )}
          </div>
        </>
      )}

      <ModalShell
        show={showCreate}
        onClose={() => setShowCreate(false)}
        size="md"
        dismissible
        title={t("todos.createBoard")}
        footer={
          <>
            <CustomButton
              variant="secondary"
              title={t("common.cancel") as string}
              onClick={() => setShowCreate(false)}
            />
            <CustomButton
              variant="primary"
              isLoading={createBoard.isLoading}
              disabled={createBoard.isLoading || !newName.trim()}
              title={t("common.create") as string}
              onClick={handleCreate}
            />
          </>
        }
      >
        <FieldLabel>{t("todos.boardName")}</FieldLabel>
        <TextInput
          type="text"
          value={newName}
          maxLength={MAX_TODO_BOARD_NAME_LENGTH}
          autoFocus
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          className="w-full"
        />
        <CharacterCounter
          currentLength={newName.length}
          maxLength={MAX_TODO_BOARD_NAME_LENGTH}
          className="mt-1"
        />
      </ModalShell>

      <ModalShell
        show={!!renaming}
        onClose={() => setRenaming(null)}
        size="md"
        dismissible
        title={t("todos.renameBoard")}
        footer={
          <>
            <CustomButton
              variant="secondary"
              title={t("common.cancel") as string}
              onClick={() => setRenaming(null)}
            />
            <CustomButton
              variant="primary"
              isLoading={renameBoard.isLoading}
              disabled={renameBoard.isLoading || !renameValue.trim()}
              title={t("common.saveShort") as string}
              onClick={handleRename}
            />
          </>
        }
      >
        <FieldLabel>{t("todos.boardName")}</FieldLabel>
        <TextInput
          type="text"
          value={renameValue}
          maxLength={MAX_TODO_BOARD_NAME_LENGTH}
          autoFocus
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleRename()}
          className="w-full"
        />
        <CharacterCounter
          currentLength={renameValue.length}
          maxLength={MAX_TODO_BOARD_NAME_LENGTH}
          className="mt-1"
        />
      </ModalShell>

      {sharingBoardId && (
        <BoardAccessModal
          show
          onClose={() => setSharingBoardId(null)}
          boardId={sharingBoardId}
          boardName={
            boards.find((b) => b.board_id === sharingBoardId)?.name ?? ""
          }
          access={boards.find((b) => b.board_id === sharingBoardId)?.access}
        />
      )}

      <ConfirmModal
        show={!!toDelete}
        onClose={() => setToDelete(null)}
        title={t("todos.deleteBoard") as string}
        description={
          t("todos.deleteBoardConfirm", { name: toDelete?.name }) as string
        }
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={() => setToDelete(null)}
        confirmBtnName={t("common.delete") as string}
        confirmBtnFn={handleDelete}
        isLoading={deleteBoard.isLoading}
        ConfirmBtnColor="red"
      />
    </div>
  );
};

export default TodoBoards;
